<template>
  <div v-if="isDataValid && isMounted" style="width: 100%; height: 100%;">
    <Pie :key="chartKey" :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { Pie } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js'
import { computed, ref, onMounted } from 'vue'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import chroma from 'chroma-js'

ChartJS.register(Title, Tooltip, Legend, ArcElement, ChartDataLabels)

const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

const props = defineProps({
  labels: {
    type: Array,
    required: true
  },
  data: {
    type: Array,
    required: true
  },
  backgroundColors: {
    type: Array,
    required: false,
    default: () => []
  },
  baseColor: {
    type: String,
    required: false,
    default: '#00423D'
  },
  title: {
    type: String,
    required: false,
    default: ''
  },
  options: {
    type: Object,
    required: false,
    default: () => ({})
  }
})

// Color generation using chroma-js (same as ExpensesChart/RevenuesChart)
const findNearestColors = (baseColor) => {
  const colorValue = baseColor || props.baseColor || '#00423D';
  const targetChroma = chroma(colorValue);
  const hue = targetChroma.get("hsl.h");
  const steps = 10;
  const stepSize = 100 / (steps - 1);
  let nearestColorsTmp = [];
  for (let i = 0; i < steps; i++) {
    const lightness = i * stepSize;
    // Avoid white (lightness 100%) and very light colors
    const adjustedLightness = lightness >= 90 ? lightness - 25 : lightness;
    nearestColorsTmp.push(`hsl(${hue}, 100%, ${adjustedLightness}%)`);
  }
  return nearestColorsTmp;
};

const chartData = computed(() => {
  // Validate data
  if (!props.data || !Array.isArray(props.data) || props.data.length === 0) {
    return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: []
      }]
    };
  }

  if (!props.labels || !Array.isArray(props.labels) || props.labels.length === 0) {
    return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: []
      }]
    };
  }

  // Ensure labels and data arrays have the same length
  const minLength = Math.min(props.data.length, props.labels.length);
  const validData = props.data.slice(0, minLength);
  const validLabels = props.labels.slice(0, minLength);
  
  // Generate colors dynamically if not provided or incomplete
  let colorsArray = [];
  if (Array.isArray(props.backgroundColors) && props.backgroundColors.length > 0) {
    // Use provided colors
    colorsArray = props.backgroundColors;
  } else {
    // Generate colors dynamically using chroma-js based on baseColor
    const nearestColors = findNearestColors(props.baseColor);
    // Start from index 1 (skip index 0) to match ExpensesChart behavior
    for (let i = 0; i < minLength; i++) {
      colorsArray.push(nearestColors[(i + 1) % nearestColors.length]);
    }
  }
  
  const validColors = [];
  for (let i = 0; i < minLength; i++) {
    validColors.push(colorsArray[i % colorsArray.length] || '#CCCCCC'); // Fallback to grey if color is missing
  }

  // Calculate total for percentage
  const total = validData.reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
  
  // If total is 0, return empty to prevent rendering
  if (total === 0) {
    return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: []
      }]
    };
  }
  
  // Calculate percentages
  const percentageData = validData.map(value => {
    const numValue = parseFloat(value) || 0;
    const percentage = total > 0 ? Math.round((numValue / total) * 100) : 0;
    return percentage;
  });

  return {
    labels: validLabels,
    datasets: [
      {
        data: percentageData,
        backgroundColor: validColors,
        borderWidth: 1
      }
    ]
  }
})

function deepMerge(target, source) {
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {};
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

const defaultOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: '#414141',
        font: { size: 14 }
      }
    },
    title: {
      display: !!props.title,
      text: props.title,
      color: '#00423D',
      font: { size: 18, weight: 'bold' }
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: function(context) {
          const percentage = context.raw;
          return `${context.label}: ${percentage} %`;
        }
      }
    },
    datalabels: {
      display: false,
      anchor: 'center',
      align: 'center',
      formatter: null
    }
  }
};

const isDataValid = computed(() => {
  return props.data && 
         Array.isArray(props.data) && 
         props.data.length > 0 && 
         props.labels && 
         Array.isArray(props.labels) && 
         props.labels.length > 0 &&
         props.data.length === props.labels.length &&
         props.data.reduce((sum, value) => sum + (parseFloat(value) || 0), 0) > 0;
});

const chartKey = computed(() => {
  // Create a unique key based on data to force re-render when data changes
  if (!isDataValid.value) return 'empty';
  return `${props.data.join('-')}-${props.labels.join('-')}-${props.baseColor}`;
});

const chartOptions = computed(() => {
  // Only compute options if data is valid
  if (!isDataValid.value) {
    return {
      responsive: true,
      plugins: {
        datalabels: { display: false }
      }
    };
  }
  
  // Merge defaultOptions with props.options (props.options takes precedence)
  const merged = deepMerge(JSON.parse(JSON.stringify(defaultOptions)), props.options);
  
  // Ensure datalabels plugin is completely disabled
  if (!merged.plugins) {
    merged.plugins = {};
  }
  
  merged.plugins.datalabels = {
    display: false,
    ...(merged.plugins.datalabels || {})
  };
  
  return merged;
});
</script>

